/* @flow */
import { BIOSPECIMEN_ORDER, SUBMITTABLE_DATA_FILES_ORDER, HARMONIZED_DATA_FILES } from 'constants/constants';
import { pluralize } from 'constants/plural';

import Column from 'components/UIKit/Flex/Column';
import { SpinnerOverlay } from 'components/UIKit/Spinner';

import {
  StyledInternalLink,
  DynamicEntityLink,
} from 'components/Links';

import CaseIcon from 'components/icons/CaseIcon';
import ClinicalIcon from 'components/icons/ClinicalIcon';
import BiospecimenIcon from 'components/icons/BiospecimenIcon';
import ReadGroupIcon from 'components/icons/ReadGroupIcon';
import AnnotationIcon from 'components/icons/AnnotationIcon';

import SideNavGroup from './SideNavGroup';
import SideNavItem from './SideNavItem';
import style from './sideNavStyle';

// $FlowIgnore
import './style.css';

class SideNav extends React.Component {
  static propTypes = {
    router: PropTypes.any,
    sidenav: PropTypes.any,
    items: PropTypes.any,
  };

  constructor(props: any): void {
    super(props);
    this.state = { activeSet: new Set() };
  }

  onFilter = (): boolean => {
    const { router: { location: { query: { fid } } } } = this.props;
    return fid;
  };

  matchesFilter = (fidFilter: string): boolean => {
    const { router: { location: { query: { fid } } } } = this.props;
    return fid && fid === fidFilter;
  };

  matchesPath = (path: string): boolean => {
    const { router: { location: { pathname } } } = this.props;
    return pathname.split('/').pop() === path;
  };

  toggleInSet = (id: string) => {
    const { activeSet } = this.state;
    activeSet[activeSet.has(id) ? 'delete' : 'add'](id);
    this.setState({ activeSet });
  };

  hasAlerts = (filterId: string): boolean => {
    const { sidenav: { data } } = this.props;
    return data[filterId] > 0;
  };

  render(): React.Element {
    const {
      isFetching,
      items,
      path,
      sidenav,
    } = this.props;
    const { activeSet } = this.state;
    const nameGenerator = name => (path === 'qc'
      ? `${name.slice(-1) === 's'
        ? name.slice(0, -1)
        : name} Errors`
      : name);

    return (
      <Column data-testid="area-side-navigation" className="SideNav no-print" style={style.navStyle}>
        <SpinnerOverlay isLoading={isFetching} style={{ width: 275 }}>
        {// TODO figure out a better way to do this without hardwiring to QC
          path === 'qc' && (
            <SideNavGroup>
              <SideNavItem
                active={this.matchesPath('qc')}
                handleClick={this.toggleInSet}
                LinkComponent={(
                  <StyledInternalLink
                    active={this.matchesPath('qc')}
                    destination="qc"
                    />
                )}
                name="Project Summary"
                style={{ padding: '1rem' }}
                />
            </SideNavGroup>
          )}

        <SideNavGroup
          customDataTestID="filter-panel-cases"
          active={activeSet.has(nameGenerator('Cases'))}
          handleClick={this.toggleInSet}
          name={nameGenerator('Cases')}
          >
          <SideNavItem
            active={this.matchesPath('case')}
            count={sidenav.data.caseCount}
            IconComponent={CaseIcon}
            LinkComponent={DynamicEntityLink("case", path)}
            name="Cases"
            onFilter={this.onFilter()}
            />
        </SideNavGroup>

        <SideNavGroup
          customDataTestID="filter-panel-clinical"
          active={activeSet.has(nameGenerator('Clinical'))}
          handleClick={this.toggleInSet}
          name={nameGenerator('Clinical')}
          >
          { ((items || {}).clinical || [])
            .filter(item => item.id !== 'clinical')
            .map(item => (
              <SideNavItem
                key={item.id}
                active={this.matchesPath(item.id)}
                IconComponent={ClinicalIcon}
                count={sidenav.data[`${item.id}Count`]}
                name={pluralize(item.id)}
                LinkComponent={DynamicEntityLink(item.id, path)}
                />
            ))}
        </SideNavGroup>

        <SideNavGroup
          customDataTestID="filter-panel-biospecimen"
          active={activeSet.has(nameGenerator('Biospecimen'))}
          handleClick={this.toggleInSet}
          name={nameGenerator('Biospecimen')}
          >
          {((items || {}).biospecimen || [])
            .sort((a, b) => BIOSPECIMEN_ORDER.indexOf(a.id) - BIOSPECIMEN_ORDER.indexOf(b.id))
            .map(item => (
              <SideNavItem
                active={this.matchesPath(`${item.id}`)}
                count={sidenav.data[`${item.id}Count`]}
                IconComponent={BiospecimenIcon}
                key={item.id}
                LinkComponent={DynamicEntityLink(item.id, path)}
                name={pluralize(item.id)}
                />
            ))}
        </SideNavGroup>

        <SideNavGroup
          customDataTestID="filter-panel-submittable-data-files"
          active={activeSet.has(nameGenerator('Submittable Data Files'))}
          handleClick={this.toggleInSet}
          name={nameGenerator('Submittable Data Files')}
          >
          { [
            ...((items || {}).data_file || []),
            ...((items || {}).metadata_file || []),
          ]
            .filter(item => item.submittable)
            .sort((a, b) => SUBMITTABLE_DATA_FILES_ORDER.indexOf(a.id)
              - SUBMITTABLE_DATA_FILES_ORDER.indexOf(b.id))
            .map((item, i) => (
              <SideNavItem
                key={`${item.id}-${i}`}
                active={this.matchesPath(`${item.id}`)}
                IconComponent={ReadGroupIcon}
                count={sidenav.data[`${item.id}Count`]}
                name={pluralize(item.id)}
                LinkComponent={DynamicEntityLink(item.id, path)}
                />
            ))}
        </SideNavGroup>

        <SideNavGroup customDataTestID="filter-panel-annotation">
          <SideNavItem
            active={this.matchesPath('annotation')}
            count={sidenav.data.annotationCount}
            IconComponent={AnnotationIcon}
            LinkComponent={DynamicEntityLink("annotation", path)}
            name={nameGenerator('Annotations')}
            />
        </SideNavGroup>

        {// TODO figure out a better way to do this without hardwiring to QC
          path === 'qc' || (
            <SideNavGroup
              customDataTestID="filter-panel-harmonized-data-files"
              active={activeSet.has('Harmonized Data Files')}
              handleClick={this.toggleInSet}
              name="Harmonized Data Files"
              >
              { [
                ...((items || {}).data_file || []),
              ]
                .filter(({ id }) => HARMONIZED_DATA_FILES.includes(id))
                .map((item, i) => (
                  <SideNavItem
                    key={`${item.id}-${i}`}
                    active={this.matchesPath(`${item.id}`)}
                    IconComponent={ReadGroupIcon}
                    count={sidenav.data[`${item.id}Count`]}
                    name={pluralize(item.id)}
                    LinkComponent={DynamicEntityLink(item.id, path)}
                    />
                ))}
            </SideNavGroup>
          )}
      </SpinnerOverlay>
      </Column>
    );
  }
}

export default SideNav;
